import { useState } from 'react'
import { LogOut, User, Shield, Lock, Laptop } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { EnhancedAvatar } from '@/components/ui/enhanced-avatar'
import { Button } from '@/components/ui/button'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useLogout, useForceLogout } from '@/features/auth/hooks/useAuth'
import { useLockStore } from '@/app/store/lock'
import type { User as UserType } from '@/types/entities'
import { useCan } from '@/hooks/useCan'
import { RestoreAgentDialog } from '@/features/restore-agent/components/RestoreAgentDialog'

interface UserAvatarMenuProps {
    user: UserType
}

export function UserAvatarMenu({ user }: UserAvatarMenuProps) {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const logout = useLogout()
    const forceLogout = useForceLogout()
    // Engineers who run restores set up their laptop from here (16 Oct 2026).
    const canRestore = useCan('recovery.execute')
    const [restoreAgentOpen, setRestoreAgentOpen] = useState(false)

    const handleLogout = () => {
        // Try normal logout first, but have force logout as backup
        logout.mutate()

        // If logout is still pending after 3 seconds, force logout
        setTimeout(() => {
            if (logout.isPending) {
                console.log('Logout taking too long, forcing logout')
                forceLogout()
            }
        }, 3000)
    }

    const handleProfile = () => {
        navigate('/profile-settings')
    }



    const getRoleBadge = (roles: UserType['roles']) => {
        if (!roles || !Array.isArray(roles)) {
            return <User className="h-3 w-3 text-gray-500" />
        }
        if (roles.some(role => role.name === 'admin')) {
            return <Shield className="h-3 w-3 text-red-500" />
        }
        if (roles.some(role => role.name === 'user')) {
            return <User className="h-3 w-3 text-blue-500" />
        }
        return <User className="h-3 w-3 text-gray-500" />
    }

    return (
        <>
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                    <EnhancedAvatar
                        name={user.name}
                        avatarUrl={user.avatarUrl}
                        avatarColor={user.avatarColor}
                        userId={user.id}
                        size="sm"
                    />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56" align="end" forceMount>
                <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                        <p className="text-sm font-medium leading-none">{user.name}</p>
                        <p className="text-xs leading-none text-muted-foreground">
                            {user.email}
                        </p>
                        <div className="flex items-center space-x-1 pt-1">
                            {getRoleBadge(user.roles)}
                            <span className="text-xs text-muted-foreground">
                                {user.roles && Array.isArray(user.roles) ? user.roles.map(role => role.name).join(', ') : 'No roles'}
                            </span>
                        </div>
                    </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleProfile}>
                    <User className="mr-2 h-4 w-4" />
                    <span>{t('profile.settings')}</span>
                </DropdownMenuItem>
                {canRestore && (
                    <DropdownMenuItem onSelect={() => setRestoreAgentOpen(true)} data-testid="menu-restore-agent">
                        <Laptop className="mr-2 h-4 w-4" />
                        <span>{t('restoreSetup.menu', 'Restore agent')}</span>
                    </DropdownMenuItem>
                )}
                {/* Above the separator with Sign out, not beside it: locking keeps the session
                    and signing out ends it, and a mis-click between them costs the user their
                    place in whatever they were doing. */}
                <DropdownMenuItem onClick={() => useLockStore.getState().lock(true)}>
                    <Lock className="mr-2 h-4 w-4" />
                    <span>{t('auth.lockScreen', 'Lock screen')}</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} disabled={logout.isPending}>
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>{t('auth.logout')}</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={forceLogout} className="text-red-600">
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>{t('auth.forceLogout')}</span>
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
        <RestoreAgentDialog open={restoreAgentOpen} onOpenChange={setRestoreAgentOpen} />
        </>
    )
}






















